import React from 'react'
import { Field, Form } from 'formik'
import { withTranslationCustom } from 'lib/i18next'
import PropTypes from 'prop-types'
import moment from 'moment'
import { flowRight } from 'lodash'
import { getVehicleVertical } from 'lib/utils/store/vehicle'
import * as Nope from 'lib/utils/validation/Nope'

import Button from 'components/UI/Button'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import Input from 'components/Forms/FormElements/Input'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import Link from 'components/Link'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import DetailsPreviewItem from 'components/UI/DetailsPreview/DetailsPreviewItem/DetailsPreviewItem'
import DetailsPreview from 'components/UI/DetailsPreview/DetailsPreview'
import AppContext from 'components/AppContext'
import DateTimeInput from 'components/Forms/FormElements/DateTimeInput'
import Checkbox from 'components/Forms/FormElements/Checkbox'

import './purchase-information.pcss'

const formSchema = Nope.schema({
    purchaseDate: Nope.date(),
    purchaseNote: Nope.message(),
    purchasePrice: Nope.priceInteger().required('formElements:validation.required'),
    purchaseSellerName: Nope.name(),
    purchasedViaVertical: Nope.boolean(),
})

class PurchaseInformation extends React.Component {
    getInitialValues () {
        const { vehicle } = this.props

        return {
            purchasePrice: vehicle.purchasePrice || '',
            purchaseSellerName: vehicle.purchaseSellerName || '',
            purchaseDate: moment(vehicle.purchaseDate || Date.now()).format('YYYY-MM-DD'),
            purchaseNote: vehicle.purchaseNote || '',
            purchasedViaVertical: vehicle.purchasedViaVertical || false,
        }
    }

    async handleSubmit (values, actions, updateVehicle) {
        const { vehicle, onSubmitted } = this.props

        await updateVehicle(vehicle.id, values)
            .then(() => {
                onSubmitted()
            })
            .catch(() => null)

        actions.setSubmitting(false)
    }

    handleCancel = () => {
        this.props.onCancel()
    }

    renderActions (isSubmitting, isEmpty) {
        const { isEditing, t } = this.props

        if (!isEditing && !isEmpty) return null

        return (
            <div className="detail-section__actions">
                <Button
                    className="button"
                    type="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    trackName="submit"
                >
                    {t('common:save')}
                </Button>
                {(isEditing && !isEmpty) && (
                    <Button
                        className="button button--secondary"
                        type="button"
                        onClick={this.handleCancel}
                        trackName="cancel"
                    >
                        {t('common:cancel')}
                    </Button>
                )}
            </div>
        )
    }

    renderFooter () {
        const { vehicle, t } = this.props
        const { createdFromBos } = vehicle

        if (!createdFromBos) return null

        return (
            <div className="purchase-information__footer">
                <div className="purchase-information__bos">
                    <Link
                        to={createdFromBos.archiveUrl}
                        className="button button--centered button--secondary detail-section__cta"
                        trackName="bosPDF"
                    >
                        {t('vehicleProfile:sections.purchase.documents.bosLink')}
                    </Link>
                    <div>{t('vehicleProfile:sections.purchase.documents.bos')}</div>
                </div>
                {createdFromBos.nettixPdfUrl && (
                    <div className="purchase-information__ad">
                        <Link
                            to={createdFromBos.nettixPdfUrl}
                            className="button button--centered button--secondary detail-section__cta"
                            trackName="nettixAdPDF"
                        >
                            {t('vehicleProfile:sections.purchase.documents.nettixPdfLink')}
                        </Link>
                        <div>{t('vehicleProfile:sections.purchase.documents.ad')}</div>
                    </div>
                )}
            </div>
        )
    }

    renderPreview (date, price, sellerName, note = null) {
        const { t } = this.props
        return (
            <DetailsPreview className="purchase-information__preview">
                <DetailsPreviewItem
                    title={t('common:date')}
                    value={moment(date).format('DD.MM.YYYY')}
                />
                <DetailsPreviewItem
                    title={t('common:purchasePrice')}
                    value={`${price} €`}
                />
                <DetailsPreviewItem
                    title={t('common:seller')}
                    value={sellerName}
                />
                {note && (
                    <DetailsPreviewItem
                        title={t('common:sellComment')}
                        value={note}
                    />
                )}
            </DetailsPreview>
        )
    }

    render () {
        const { isEditing, t } = this.props
        const { vehicle } = this.props
        const { createdFromBos } = vehicle

        const isEmpty = !createdFromBos && !vehicle.purchaseDate

        return (
            <AppContext context="purchaseInformation">
                <VehicleProfileLogic>
                    {({ updateVehicle, error, loading }) => (
                        <div className="purchase-information">
                            {(!isEditing && !isEmpty) ? (
                                createdFromBos ? (
                                    this.renderPreview(
                                        createdFromBos.date,
                                        createdFromBos.pricePayment.totalPrice,
                                        `${createdFromBos.seller.sellerFirstName} ${createdFromBos.seller.sellerLastName}`
                                    )
                                ) : (
                                    this.renderPreview(
                                        vehicle.purchaseDate,
                                        vehicle.purchasePrice,
                                        vehicle.purchaseSellerName,
                                        vehicle.purchaseNote
                                    )
                                )
                            ) : (
                                <ExtendedFormik
                                    id="purchase-info-form"
                                    validationSchema={formSchema}
                                    initialValues={this.getInitialValues()}
                                    onSubmit={(values, actions) => this.handleSubmit(values, actions, updateVehicle)}
                                >
                                    {({ isSubmitting }) => (
                                        <Form noValidate>
                                            <FormElementGroup cssClass="cols-2 cols-2--static">
                                                <Field
                                                    type="number"
                                                    name="purchasePrice"
                                                    label={t('common:purchasePrice')}
                                                    component={Input}
                                                />
                                                <Field
                                                    type="text"
                                                    name="purchaseSellerName"
                                                    label={t('common:seller')}
                                                    component={Input}
                                                />
                                            </FormElementGroup>
                                            <FormElementGroup cssClass="cols-2 cols-2--static">
                                                <Field
                                                    name="purchaseDate"
                                                    type="text"
                                                    label={t('common:date')}
                                                    dateFormat={false}
                                                    displayTime={false}
                                                    disableSetCurrentTime
                                                    component={DateTimeInput}
                                                />
                                                <Field
                                                    type="text"
                                                    name="purchaseNote"
                                                    label={t('common:sellComment')}
                                                    component={Input}
                                                />
                                            </FormElementGroup>
                                            <FormElementGroup>
                                                <Field name="purchasedViaVertical">
                                                    {(props) => (
                                                        <Checkbox
                                                            {...props}
                                                            label={t('vehicleProfile:sections.purchase.purchasedViaVertical', { vertical: t(`vehicles:vertical.${getVehicleVertical(vehicle)}.name`) })}
                                                        />
                                                    )}
                                                </Field>
                                            </FormElementGroup>
                                            {this.renderActions(isSubmitting, isEmpty)}
                                        </Form>
                                    )}
                                </ExtendedFormik>
                            )}
                            {this.renderFooter()}
                        </div>
                    )}
                </VehicleProfileLogic>
            </AppContext>
        )
    }
}

PurchaseInformation.propTypes = {
    vehicle: PropTypes.object.isRequired,
    onSubmitted: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isEditing: PropTypes.bool,
}

export default flowRight(
    withTranslationCustom(['common', 'vehicleProfile'])
)(PurchaseInformation)
